/**
 * Hook for managing billing operations
 * Fetches balance, stats, and handles deposits
 */
import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { modalApi } from '@/services/modalApi';

export interface BillingBalance {
  balance: number;
  total_deposited: number;
}

export interface BillingStats {
  users_count: number;
  sessions_count: number;
  designs_count: number;
  images_count: number;
  image_edit_count: number;
  mockup_count: number;
  total_cost: number;
}

export function useBilling() {
  const [balance, setBalance] = useState<BillingBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for balance changes
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'billing_account', 'main'),
      async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setBalance({
            balance: data.balance || 0,
            total_deposited: data.total_deposited || 0
          });
        } else {
          // Initialize if not exists
          try {
            const balanceData = await modalApi.getBillingBalance();
            setBalance({
              balance: balanceData.balance || 0,
              total_deposited: balanceData.total_deposited || 0
            });
          } catch (err: any) {
            setError(err.message);
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error('Billing balance listener error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const fetchBalance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await modalApi.getBillingBalance();
      setBalance({
        balance: data.balance || 0,
        total_deposited: data.total_deposited || 0
      });
      return data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const depositMoney = useCallback(async (amount: number, adminUserId: string) => {
    try {
      setError(null);
      const result = await modalApi.depositMoney(amount, adminUserId);
      // Balance will update automatically via real-time listener
      return result;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, []);

  const getStats = useCallback(async (
    startDate?: string,
    endDate?: string,
    visitorId?: string
  ): Promise<BillingStats> => {
    try {
      setError(null);
      const data = await modalApi.getBillingStats(startDate, endDate, visitorId);
      return {
        users_count: data.users_count || 0,
        sessions_count: data.sessions_count || 0,
        designs_count: data.designs_count || 0,
        images_count: data.images_count || 0,
        image_edit_count: data.image_edit_count || 0,
        mockup_count: data.mockup_count || 0,
        total_cost: data.total_cost || 0
      };
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    balance,
    loading,
    error,
    fetchBalance,
    depositMoney,
    getStats
  };
}

